<template>
  <div class="w-full">
    <div ref="chartRef" :style="{ height: chartHeight }" class="w-full"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onBeforeUnmount } from "vue";
import * as echarts from "echarts";

// bars: [{ conceptNumber, title, levelCounts: {0,1,2,3}, assessedTotal }]
// segments: [{ level, color, label }] ordered low -> high proficiency
const props = defineProps({
  bars: { type: Array, default: () => [] },
  segments: { type: Array, default: () => [] },
});

const emit = defineEmits(["selectSegment"]);

const chartRef = ref(null);
let chartInstance = null;

// Fixed row height keeps the bar thickness + spacing identical no matter how
// many concepts a thread has (the card just grows/shrinks in height).
const ROW_HEIGHT = 40;
const chartHeightPx = computed(() => Math.max(props.bars.length * ROW_HEIGHT, 120));
const chartHeight = computed(() => `${chartHeightPx.value}px`);

const buildOptions = () => {
  const categories = props.bars.map((bar) => bar.title || `Concept ${bar.conceptNumber}`);

  // Each level is drawn as its OWN bar from 0 to its cumulative end (not a
  // single stack), overlapped at the same position and drawn back-to-front, each
  // with a rounded right cap. This gives every segment a rounded right edge,
  // with a square left start and no gaps between segments.
  const RADIUS = 6;
  const n = props.segments.length;
  const series = props.segments.map((segment, segIdx) => ({
    name: segment.label,
    type: "bar",
    barGap: "-100%", // overlap all series at the same y position
    barMaxWidth: 25,
    color: segment.color,
    z: n - segIdx, // lower levels (left) sit on top of higher ones
    data: props.bars.map((bar) => {
      const total = bar.assessedTotal;
      const count = bar.levelCounts[segment.level] || 0;
      const segPct = total > 0 ? (count / total) * 100 : 0;
      // Cumulative % from the first segment up to and including this one.
      let cum = 0;
      for (let i = 0; i <= segIdx; i++) {
        cum += bar.levelCounts[props.segments[i].level] || 0;
      }
      const cumPct = total > 0 ? (cum / total) * 100 : 0;
      // Carry the segment's own count/% for the tooltip (value is cumulative).
      return {
        value: cumPct,
        count,
        segPct,
        itemStyle: { borderRadius: [0, RADIUS, RADIUS, 0] },
      };
    }),
  }));

  return {
    grid: { top: 8, left: 4, right: 8, bottom: 4, containLabel: true },
    xAxis: {
      type: "value",
      max: 100,
      show: false,
    },
    yAxis: {
      type: "category",
      data: categories,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: "#475467",
        fontSize: 12,
        width: 140,
        overflow: "truncate",
      },
    },
    tooltip: {
      trigger: "item",
      formatter: (params) => {
        const count = params.data && params.data.count != null ? params.data.count : 0;
        const segPct = params.data && params.data.segPct != null ? params.data.segPct : 0;
        return `${params.name}<br/>${params.marker}${params.seriesName}: <b>${count}</b> (${Math.round(segPct)}%)`;
      },
    },
    series,
  };
};

// Resize with an EXPLICIT height (derived from the concept count) so ECharts
// never lays out against a stale container height when switching threads.
const resizeChart = () => {
  if (chartInstance) chartInstance.resize({ width: "auto", height: chartHeightPx.value });
};

const renderChart = () => {
  if (!chartRef.value) return;
  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value);
    chartInstance.on("click", (params) => {
      const bar = props.bars[params.dataIndex];
      const segment = props.segments[params.seriesIndex];
      if (bar && segment) {
        emit("selectSegment", { conceptNumber: bar.conceptNumber, level: segment.level });
      }
    });
  }
  chartInstance.setOption(buildOptions(), true);
  resizeChart();
};

onMounted(() => {
  renderChart();
  window.addEventListener("resize", resizeChart);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeChart);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});

// flush: "post" runs the re-render after the DOM (and the bound height) update.
watch(
  () => [props.bars, props.segments],
  () => renderChart(),
  { deep: true, flush: "post" }
);
</script>
